import Papa from 'papaparse';

/**
 * Header spellings accepted for the one required column. Every other column
 * keeps its own name as the merge-field token, so the tokens a user can insert
 * are exactly the columns in their file — no invented or empty fields.
 */
const EMAIL_ALIASES = ['email', 'e-mail', 'email address', 'emailaddress', 'mail', 'to'];

export interface ParsedRow {
  /** 1-based row number in the source file, counting the header. */
  rowNumber: number;
  email: string;
  mergeData: Record<string, string>;
}

export interface RowProblem {
  rowNumber: number;
  /** Raw value that caused the problem, for display in the error table. */
  value: string;
  reason: string;
}

export interface CsvParseResult {
  rows: ParsedRow[];
  /** Rows dropped because they were unusable. */
  errors: RowProblem[];
  /** Rows dropped because the address already appeared earlier in the file. */
  duplicates: RowProblem[];
  /** Canonical + passthrough column names available as merge fields. */
  fields: string[];
}

function normalize(value: string): string {
  return value.trim().toLowerCase().replace(/[\s_-]+/g, ' ');
}

/**
 * Email aliases are normalized with the same rule as incoming headers, so an
 * entry written `e-mail` still matches a column headed `E_Mail` or `E Mail`.
 */
const NORMALIZED_EMAIL_ALIASES = EMAIL_ALIASES.map(normalize);

function normalizeHeader(raw: string): string {
  const cleaned = normalize(raw);
  if (NORMALIZED_EMAIL_ALIASES.includes(cleaned)) return 'email';
  // Every other column becomes a merge field under a safe token of its own name.
  return cleaned.replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');
}

/**
 * Deliberately permissive: this rejects the shapes that will certainly bounce
 * (no @, whitespace, missing TLD) without trying to out-guess the RFC.
 */
export function isValidEmail(value: string): boolean {
  const trimmed = value.trim();
  if (!trimmed || /\s/.test(trimmed)) return false;
  return /^[^@]+@[^@.]+(\.[^@.]+)+$/.test(trimmed);
}

/** Strips a display name: `Jane Doe <jane@x.com>` -> `jane@x.com`. */
export function extractAddress(value: string): string {
  const angled = value.match(/<([^>]+)>/);
  return (angled ? angled[1] : value).trim();
}

export function parseRecipientCsv(input: string): CsvParseResult {
  const parsed = Papa.parse<Record<string, string>>(input, {
    header: true,
    skipEmptyLines: 'greedy',
    transformHeader: normalizeHeader,
  });

  const rows: ParsedRow[] = [];
  const errors: RowProblem[] = [];
  const duplicates: RowProblem[] = [];
  const seen = new Set<string>();

  const fields = (parsed.meta.fields ?? []).filter(Boolean);
  if (!fields.includes('email')) {
    return {
      rows: [],
      errors: [
        {
          rowNumber: 1,
          value: fields.join(', ') || '(no columns found)',
          reason:
            'No email column found. Add a column headed "Email" (or "E-mail", "Email Address").',
        },
      ],
      duplicates: [],
      fields,
    };
  }

  parsed.data.forEach((record, index) => {
    // +2: one for the header row, one to make it 1-based like a spreadsheet.
    const rowNumber = index + 2;
    const rawEmail = (record.email ?? '').trim();

    if (!rawEmail) {
      errors.push({ rowNumber, value: '', reason: 'Missing email address' });
      return;
    }

    const email = extractAddress(rawEmail).toLowerCase();
    if (!isValidEmail(email)) {
      errors.push({ rowNumber, value: rawEmail, reason: 'Not a valid email address' });
      return;
    }

    if (seen.has(email)) {
      duplicates.push({ rowNumber, value: email, reason: 'Duplicate of an earlier row' });
      return;
    }
    seen.add(email);

    const mergeData: Record<string, string> = {};
    for (const field of fields) {
      mergeData[field] = (record[field] ?? '').trim();
    }

    rows.push({ rowNumber, email, mergeData });
  });

  return { rows, errors, duplicates, fields };
}
